import { useEffect } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

interface UseInfiniteFilesArgs {
  filesLength: number;
  loading: boolean;
  loadingMoreFiles: boolean;
  page: number;
  totalFiles: number;
  loadingMoreFilesRef: MutableRefObject<boolean>;
  setPage: Dispatch<SetStateAction<number>>;
  getFiles: (pageToLoad: number) => Promise<void>;
}

export const useInfiniteFiles = ({
  filesLength,
  loading,
  loadingMoreFiles,
  page,
  totalFiles,
  loadingMoreFilesRef,
  setPage,
  getFiles,
}: UseInfiniteFilesArgs) => {
  useEffect(() => {
    const loadNextPage = async () => {
      if (
        loadingMoreFilesRef.current ||
        loadingMoreFiles ||
        loading ||
        filesLength >= totalFiles
      ) {
        return;
      }

      const nextPage = page + 1;
      loadingMoreFilesRef.current = true;
      setPage(nextPage);
      await getFiles(nextPage);
    };

    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const loadThreshold = document.documentElement.scrollHeight - 300;

      if (scrollPosition >= loadThreshold) {
        loadNextPage();
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // getFiles uses the current folder/view from this render. The scroll listener
    // is intentionally rebound when paging inputs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesLength, loading, loadingMoreFiles, page, totalFiles]);
};
